import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  DeleteIcon,
  DuplicateIcon,
  FilterIcon,
  InsertLeftIcon,
  InsertRightIcon,
  MoveLeftIcon,
  MoveRightIcon,
  SortIcon,
  ViewIcon,
} from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import {
  inputConfig,
  propertyDeleteConfirmMessage,
  propertyDeleteFallbackName,
  propertyTypeLabel,
  typeConfig,
} from '../../../../core/common/property-menu.js';
import { renderReadonlyLock } from '../../../../core/component/column-header/column-header.js';
import { filterTraitKey } from '../../../../core/filter/trait.js';
import { firstFilterByRef } from '../../../../core/filter/utils.js';
import { dataViewI18nT } from '../../../../core/i18n.js';
import { renderUniLit } from '../../../../core/index.js';
import { sortTraitKey } from '../../../../core/sort/manager.js';
import { createSortUtils } from '../../../../core/sort/utils.js';
import {
  draggable,
  dragHandler,
  droppable,
} from '../../../../core/utils/wc-dnd/dnd-context.js';
import type { Property } from '../../../../core/view-manager/property.js';
import { numberFormats } from '../../../../property-presets/number/utils/formats.js';
import {
  createDefaultShowQuickSettingBar,
  ShowQuickSettingBarKey,
} from '../../../../widget-presets/quick-setting-bar/context.js';
import { DEFAULT_COLUMN_TITLE_HEIGHT } from '../../consts.js';
import type {
  TableProperty,
  TableSingleView,
} from '../../table-view-manager.js';
import type { TableViewUILogic } from '../table-view-ui-logic.js';
import {
  getTableGroupRect,
  getVerticalIndicator,
  startDragWidthAdjustmentBar,
} from './vertical-indicator.js';

// StreamLain Extension Hook Type (registered via window global by StreamLain).
// Visibility is owned by the returned subMenu's own `hide` closure (signal
// reactive). No pre-filter here — the items array is frozen at popMenu time.
interface StreamlainColumnMenuHook {
  name: string;
  getSubMenu: (
    column: TableProperty,
    tableViewManager: TableSingleView
  ) => ReturnType<typeof menu.subMenu>;
  /**
   * Fired immediately after the user picks a new type for this column.
   * Relation / Rollup etc. use it to auto-open their config popup so the
   * user doesn't have to re-enter the column menu just to configure.
   */
  onAfterTypeChange?: (
    column: TableProperty,
    tableViewManager: TableSingleView,
    newType: string,
    anchor: HTMLElement
  ) => void;
}
interface WindowWithStreamlainHooks extends Window {
  __streamlainColumnMenuHooks?: StreamlainColumnMenuHook[];
}

export class DatabaseHeaderColumn extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    affine-database-header-column {
      display: flex;
      position: relative;
    }

    .affine-database-header-column-grabbing * {
      cursor: grabbing;
    }
  `;

  private readonly _clickColumn = () => {
    if (this.tableViewManager.readonly$.value) {
      return;
    }
    this.popMenu();
  };

  private readonly _clickTypeIcon = (event: MouseEvent) => {
    if (this.tableViewManager.readonly$.value) {
      return;
    }
    if (this.column.type$.value === 'title' || !this.column.typeCanSet) {
      return;
    }
    event.stopPropagation();
    popMenu(popupTargetFromElement(this), {
      options: {
        preferDesktop: true,
        items: this.tableViewManager.propertyMetas$.value.map(config => {
          return menu.action({
            name: propertyTypeLabel(config.type, config.config.name),
            isSelected: config.type === this.column.type$.value,
            prefix: renderUniLit(config.renderer.icon),
            class: { 'data-view-menu-entry': true },
            select: () => {
              this.column.typeSet?.(config.type);
              const hooks = (window as WindowWithStreamlainHooks)
                .__streamlainColumnMenuHooks;
              const anchor = this;
              hooks?.forEach(hook => {
                hook.onAfterTypeChange?.(
                  this.column,
                  this.tableViewManager,
                  config.type,
                  anchor
                );
              });
            },
          });
        }),
      },
    });
  };

  private readonly _contextMenu = (e: MouseEvent) => {
    if (this.tableViewManager.readonly$.value) {
      return;
    }
    e.preventDefault();
    this.popMenu(e.currentTarget as HTMLElement);
  };

  private readonly _enterWidthDragBar = () => {
    if (this.tableViewManager.readonly$.value) {
      return;
    }
    if (this.drawWidthDragBarTask) {
      cancelAnimationFrame(this.drawWidthDragBarTask);
      this.drawWidthDragBarTask = 0;
    }
    this.drawWidthDragBar();
  };

  private readonly _leaveWidthDragBar = () => {
    cancelAnimationFrame(this.drawWidthDragBarTask);
    this.drawWidthDragBarTask = 0;
    getVerticalIndicator().remove();
  };

  private readonly drawWidthDragBar = () => {
    const rect = getTableGroupRect(this);
    if (!rect) {
      return;
    }
    getVerticalIndicator().display(
      this.getBoundingClientRect().right,
      rect.top,
      rect.bottom - rect.top
    );
    this.drawWidthDragBarTask = requestAnimationFrame(this.drawWidthDragBar);
  };

  private drawWidthDragBarTask = 0;

  private readonly widthDragBar = createRef();

  editTitle = () => {
    this._clickColumn();
  };

  private get readonly() {
    return this.tableViewManager.readonly$.value;
  }

  private _addFilter() {
    const filterTrait = this.tableViewManager.traitGet(filterTraitKey);
    if (!filterTrait) return;

    const filter = firstFilterByRef(this.tableViewManager.vars$.value, {
      type: 'ref',
      name: this.column.id,
    });

    filterTrait.filterSet({
      type: 'group',
      op: 'and',
      conditions: [filter, ...filterTrait.filter$.value.conditions],
    });

    this._toggleQuickSettingBar();
  }

  private _addSort(desc: boolean) {
    const sortTrait = this.tableViewManager.traitGet(sortTraitKey);
    if (!sortTrait) return;

    const sortUtils = createSortUtils(
      sortTrait,
      this.tableViewLogic.eventTrace
    );
    const sortList = sortUtils.sortList$.value;
    const existingIndex = sortList.findIndex(
      sort => sort.ref.name === this.column.id
    );

    if (existingIndex !== -1) {
      sortUtils.change(existingIndex, {
        ref: { type: 'ref', name: this.column.id },
        desc,
      });
    } else {
      sortUtils.add({
        ref: { type: 'ref', name: this.column.id },
        desc,
      });
    }

    this._toggleQuickSettingBar();
  }

  private _toggleQuickSettingBar(show = true) {
    const map = this.tableViewManager.serviceGetOrCreate(
      ShowQuickSettingBarKey,
      createDefaultShowQuickSettingBar
    );
    map.value = {
      ...map.value,
      [this.tableViewManager.id]: show,
    };
  }

  private popMenu(ele?: HTMLElement) {
    const enableNumberFormatting =
      this.tableViewManager.featureFlags$.value.enable_number_formatting;
    const schemaReadonly = this.column.readonly$.value;

    popMenu(popupTargetFromElement(ele ?? this), {
      options: {
        preferDesktop: true,
        items: [
          ...(schemaReadonly
            ? []
            : [inputConfig(this.column), typeConfig(this.column)]),
          // Number format begin
          ...(enableNumberFormatting && !schemaReadonly
            ? [
                menu.subMenu({
                  name: dataViewI18nT('property.numberFormat'),
                  hide: () =>
                    !this.column.dataUpdate ||
                    this.column.type$.value !== 'number',
                  options: {
                    items: [
                      numberFormatConfig(this.column),
                      ...numberFormats.map(format => {
                        const data = this.column.data$.value;
                        return menu.action({
                          isSelected: data.format === format.type,
                          prefix: html`<span
                            style="font-size: var(--streamlain-font-base); scale: 1.2;"
                            >${format.symbol}</span
                          >`,
                          name: format.label,
                          select: () => {
                            if (data.format === format.type) return;
                            this.column.dataUpdate(() => ({
                              format: format.type,
                            }));
                          },
                        });
                      }),
                    ],
                  },
                }),
              ]
            : []),
          // Number format end
          // StreamLain Extension Hooks (registered via window global).
          // Note: we do NOT pre-filter by hook.shouldShow here — the items
          // array is built once at popMenu time, so pre-filtering would
          // freeze visibility at that moment. Each subMenu's own `hide`
          // closure is signal-reactive and handles visibility at render.
          ...(
            (typeof window !== 'undefined'
              ? (window as WindowWithStreamlainHooks)
                  .__streamlainColumnMenuHooks
              : undefined
            )?.map(hook =>
              hook.getSubMenu(this.column, this.tableViewManager)
            ) ?? []
          ).filter(() => !schemaReadonly),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.hideInView'),
                prefix: ViewIcon(),
                hide: () => !this.column.hideCanSet,
                select: () => {
                  this.column.hideSet(true);
                },
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.filter'),
                prefix: FilterIcon(),
                select: () => this._addFilter(),
              }),
              menu.action({
                name: dataViewI18nT('property.sortAscending'),
                prefix: SortIcon(),
                select: () => this._addSort(false),
              }),
              menu.action({
                name: dataViewI18nT('property.sortDescending'),
                prefix: SortIcon(),
                select: () => this._addSort(true),
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.insertLeft'),
                prefix: InsertLeftIcon(),
                select: () => {
                  this.tableViewManager.propertyAdd({
                    id: this.column.id,
                    before: true,
                  });
                  Promise.resolve()
                    .then(() => {
                      const pre =
                        this.previousElementSibling?.previousElementSibling;
                      if (pre instanceof DatabaseHeaderColumn) {
                        pre.editTitle();
                        pre.scrollIntoView({
                          inline: 'nearest',
                          block: 'nearest',
                        });
                      }
                    })
                    .catch(console.error);
                },
              }),
              menu.action({
                name: dataViewI18nT('property.insertRight'),
                prefix: InsertRightIcon(),
                select: () => {
                  this.tableViewManager.propertyAdd({
                    id: this.column.id,
                    before: false,
                  });
                  Promise.resolve()
                    .then(() => {
                      const next = this.nextElementSibling?.nextElementSibling;
                      if (next instanceof DatabaseHeaderColumn) {
                        next.editTitle();
                        next.scrollIntoView({
                          inline: 'nearest',
                          block: 'nearest',
                        });
                      }
                    })
                    .catch(console.error);
                },
              }),
              menu.action({
                name: dataViewI18nT('property.moveLeft'),
                prefix: MoveLeftIcon(),
                hide: () => this.column.isFirst$.value,
                select: () => {
                  const prev = this.column.prev$.value;
                  if (!prev) {
                    return;
                  }
                  this.column.move({
                    id: prev.id,
                    before: true,
                  });
                },
              }),
              menu.action({
                name: dataViewI18nT('property.moveRight'),
                prefix: MoveRightIcon(),
                hide: () => this.column.isLast$.value,
                select: () => {
                  const next = this.column.next$.value;
                  if (!next) {
                    return;
                  }
                  this.column.move({
                    id: next.id,
                    before: false,
                  });
                },
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.duplicate'),
                prefix: DuplicateIcon(),
                hide: () => !this.column.canDuplicate,
                select: () => {
                  this.column.duplicate?.();
                },
              }),
              menu.action({
                name: dataViewI18nT('property.delete'),
                prefix: DeleteIcon(),
                hide: () => !this.column.canDelete,
                select: (): void | false => {
                  // sl-luoo: Confirm before deleting a column — this permanently
                  // removes the column and ALL cell data across every row.
                  //
                  // Relation columns route through the propertyDelete
                  // interceptor in database-block-config-service, which shows
                  // our own ConfirmModal — a specialised one for
                  // bi-directional (delete both sides / single) and a simple
                  // one for self-relations. Skipping the native confirm here
                  // avoids the double-dialog experience users saw before.
                  if (this.column.type$.value === 'relation') {
                    this.column.delete?.();
                    return;
                  }
                  const name =
                    this.column.name$.value || propertyDeleteFallbackName();
                  if (!confirm(propertyDeleteConfirmMessage(name))) {
                    return false; // keep menu open
                  }
                  this.column.delete?.();
                },
                class: {
                  'delete-item': true,
                },
              }),
            ],
          }),
        ],
      },
    });
  }

  private widthDragStart(event: PointerEvent) {
    startDragWidthAdjustmentBar(
      event,
      this,
      this.getBoundingClientRect().width,
      this.column
    );
  }

  private readonly _onWidthDragPointerDown = (event: PointerEvent) => {
    if (this.tableViewManager.readonly$.value) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.widthDragStart(event);
  };

  override connectedCallback() {
    super.connectedCallback();
    const table = this.closest('dv-table-view-ui');
    if (table) {
      this.disposables.add(
        table.logic.handleEvent('dragStart', context => {
          if (this.tableViewManager.readonly$.value) {
            return;
          }
          const event = context.get('pointerState').raw;
          const target = event.target;
          if (
            target instanceof Element &&
            this.widthDragBar.value?.contains(target)
          ) {
            event.preventDefault();
            event.stopPropagation();
            this.widthDragStart(event);
            return true;
          }
          return false;
        })
      );
    }
  }

  override render() {
    const column = this.column;
    const style = styleMap({
      height: `calc(${DEFAULT_COLUMN_TITLE_HEIGHT}px - 2px)`,
      margin: '1px 0',
    });
    const classes = classMap({
      'affine-database-column-move': true,
      [this.grabStatus]: true,
    });
    return html`
      <div
        style=${style}
        class="affine-database-column-content header-column"
        @click="${this._clickColumn}"
        @contextmenu="${this._contextMenu}"
        ${dragHandler(column.id)}
        ${draggable(column.id)}
        ${droppable(column.id)}
      >
        ${this.readonly
          ? null
          : html` <button class="${classes}">
              <div class="hover-trigger"></div>
              <div class="control-h"></div>
              <div class="control-l"></div>
              <div class="control-r"></div>
            </button>`}
        <div class="affine-database-column-text ${column.type$.value}">
          <div
            class="affine-database-column-type-icon dv-hover"
            @click="${this._clickTypeIcon}"
          >
            <uni-lit .uni="${column.icon}"></uni-lit>
          </div>
          <div class="affine-database-column-text-content">
            <div class="affine-database-column-text-input column-name">
              ${column.name$.value}
            </div>
            ${renderReadonlyLock(column)}
          </div>
        </div>
      </div>
      <div
        ${ref(this.widthDragBar)}
        @mouseenter="${this._enterWidthDragBar}"
        @mouseleave="${this._leaveWidthDragBar}"
        @pointerdown="${this._onWidthDragPointerDown}"
        style="position: absolute;right: -4px;top: 0;width: 8px;height: 100%;z-index: 2;cursor: col-resize"
      ></div>
    `;
  }

  @property({ attribute: false })
  accessor column!: TableProperty;

  @property({ attribute: false })
  accessor grabStatus: 'grabStart' | 'grabEnd' | 'grabbing' = 'grabEnd';

  @property({ attribute: false })
  accessor tableViewLogic!: TableViewUILogic;

  get tableViewManager() {
    return this.tableViewLogic.view;
  }
}

function numberFormatConfig(column: Property) {
  return () =>
    html` <affine-database-number-format-bar
      .column="${column}"
    ></affine-database-number-format-bar>`;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-database-header-column': DatabaseHeaderColumn;
  }
}
